import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ControlType,
  InputComponent,
  TextInputDef,
} from '@domgen/dgx-fe-dynamic-form-builder';
import { BehaviorSubject, filter, last, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Component({
  selector: 'sky-imei-section',
  templateUrl: './imei-section.component.html',
  styleUrls: ['./imei-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IMEISectionComponent {
  private IMEIPattern = /^[0-9]{15}$/;
  private isImeiInvalidSubject = new BehaviorSubject<ValidationErrors | null>(
    null
  );
  public isImeiInvalid$ = this.isImeiInvalidSubject.asObservable();

  @ViewChild('inputApi', { static: false }) inputApi:
    | InputComponent
    | undefined;

  @Input() set isImeiInvalid(value: boolean) {
    this.isImeiInvalidSubject.next(value ? { invalidAsync: true } : null);
    this.inputApi?.onChange({
      target: { value: this.formGroup.get('searchIMEI')?.value },
    });
  }

  @Input() set imeiNumbersInBasket(numbers: string[]) {
    if (numbers && numbers.length) {
      this.formGroup.controls['searchIMEI'].addValidators(
        this.isExistingImeiValidator(numbers)
      );
      this.formGroup.controls['searchIMEI'].updateValueAndValidity();
    }
  }

  @Output() search: EventEmitter<string> = new EventEmitter();

  field = {
    controlType: ControlType.INPUT,
    controlName: 'searchIMEI',
    placeholder: 'Enter IMEI',
    validationMessages: [
      { type: 'required', message: 'IMEI is required' },
      { type: 'pattern', message: 'Please enter a valid IMEI number' },
      {
        type: 'duplicateImei',
        message:
          'The IMEI you entered belongs to another mobile. Check the IMEI of the mobile you want to cover',
      },
      {
        type: 'invalidAsync',
        message: `We couldn't find your IMEI number. Please check your mobile and try again.
      If this problem continues, you can call us on 0800 561 4457.
      (Calls may be recorded and monitored for quality and training purposes. Lines open 8am – 11pm 7 days a week).`,
      },
    ],
  } as TextInputDef;

  public formGroup = new UntypedFormGroup({
    searchIMEI: new UntypedFormControl(
      '',
      [
        Validators.required,
        Validators.pattern(this.IMEIPattern),
        this.isExistingImeiValidator(this.imeiNumbersInBasket),
      ],
      this.asyncRemoteValidation(this.isImeiInvalid$)
    ),
  });

  searchIMEI(isDisabled: boolean) {
    if (isDisabled) return;

    this.search.emit(this.formGroup.value.searchIMEI);
  }

  asyncRemoteValidation(
    isImeiInvalid$: Observable<ValidationErrors | null>
  ): AsyncValidatorFn {
    return (): Observable<ValidationErrors | null> => {
      return isImeiInvalid$.pipe(first());
    };
  }

  isExistingImeiValidator(imeiNumbersInBasket: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const alreadyExist =
        imeiNumbersInBasket &&
        imeiNumbersInBasket.length &&
        imeiNumbersInBasket.some((imei) => imei === control.value);
      return alreadyExist ? { duplicateImei: true } : null;
    };
  }
}
